export const MORTGAGE_LOAN_TERMS = [30, 15, 5] as const;
export type MortgageLoanTerm = (typeof MORTGAGE_LOAN_TERMS)[number];

export interface MortgageEstimateInput {
  homePrice: number;
  downPaymentPercent: number;
  annualInterestRate: number;
  termYears: MortgageLoanTerm;
}

export interface MortgageEstimate {
  downPaymentAmount: number;
  loanAmount: number;
  monthlyPrincipalAndInterest: number;
}

/** Pure principal-and-interest estimate shared by any satellite surface. */
export function calculateMortgageEstimate(
  input: MortgageEstimateInput,
): MortgageEstimate | null {
  const { homePrice, downPaymentPercent, annualInterestRate, termYears } = input;
  if (
    !Number.isFinite(homePrice) ||
    homePrice <= 0 ||
    !Number.isFinite(downPaymentPercent) ||
    downPaymentPercent < 0 ||
    downPaymentPercent > 100 ||
    !Number.isFinite(annualInterestRate) ||
    annualInterestRate < 0 ||
    !MORTGAGE_LOAN_TERMS.includes(termYears)
  ) {
    return null;
  }

  const downPaymentAmount = homePrice * (downPaymentPercent / 100);
  const loanAmount = homePrice - downPaymentAmount;
  const payments = termYears * 12;
  const monthlyRate = annualInterestRate / 100 / 12;
  const monthlyPrincipalAndInterest =
    monthlyRate === 0
      ? loanAmount / payments
      : (loanAmount * monthlyRate * (1 + monthlyRate) ** payments) /
        ((1 + monthlyRate) ** payments - 1);

  return {
    downPaymentAmount,
    loanAmount,
    monthlyPrincipalAndInterest,
  };
}
